import { useCallback, useEffect, useState } from 'react';
import useDragAndDrop from 'src/hooks/useDragAndDrop';
import useMediaQuery from 'src/hooks/useMediaQuery';

interface IUseResizableNotesFormProps {
  visible: boolean;
  containerResizable: HTMLDivElement | null;
  elementToDrag: HTMLDivElement | null;
  textAreaToResize: HTMLTextAreaElement | null;
  maxTopPosition: number;
}

interface IUseResizableNotesFormResult {
  textAreaHeight: number;
  textAreaMaxHeight: number;
  isDragging: boolean;
  setIsMouseDownOnElementDrag: (value: boolean) => void;
  handleMouseMove: (event: React.MouseEvent<HTMLElement>) => void;
}

export const useResizableNotesForm = ({
  visible,
  maxTopPosition,
  containerResizable,
  textAreaToResize,
  elementToDrag,
}: IUseResizableNotesFormProps): IUseResizableNotesFormResult => {
  const { isDragging } = useDragAndDrop();
  const { windowHeight } = useMediaQuery({});
  const [isMouseDownOnElementDrag, setIsMouseDownOnElementDrag] = useState(false);
  const [textAreaHeight, setTextAreaHeight] = useState(0);
  const [textAreaMaxHeight, setTextAreaMaxHeight] = useState(0);

  useEffect(() => {
    if (!isDragging) {
      setIsMouseDownOnElementDrag(false);
    }
  }, [isDragging]);

  useEffect(() => {
    const calcTextAreaMaxHeight = () => {
      if (textAreaToResize && containerResizable && visible) {
        const distanceBetweenWindowHeightAndMaxTopPosition = windowHeight - maxTopPosition;

        const distanceBetweenTextAreaTopAndContainerTop =
          textAreaToResize.getBoundingClientRect().top - containerResizable.getBoundingClientRect().top;

        const distanceBetweenContainerBottomAndTextAreaBottom =
          containerResizable.getBoundingClientRect().bottom - textAreaToResize.getBoundingClientRect().bottom;

        setTextAreaMaxHeight(
          distanceBetweenWindowHeightAndMaxTopPosition -
            distanceBetweenTextAreaTopAndContainerTop -
            distanceBetweenContainerBottomAndTextAreaBottom,
        );
        return;
      }
      setTextAreaMaxHeight(0);
    };

    calcTextAreaMaxHeight();
  }, [containerResizable, maxTopPosition, textAreaToResize, visible, windowHeight]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const positionY = event.clientY;
      if (isMouseDownOnElementDrag && containerResizable && elementToDrag && textAreaToResize) {
        const distanceBetweenElementToDragAndMaxTopPosition =
          maxTopPosition - (positionY - elementToDrag.getBoundingClientRect().height / 2);

        const partialHeight =
          distanceBetweenElementToDragAndMaxTopPosition >= 0
            ? containerResizable.getBoundingClientRect().top - maxTopPosition
            : containerResizable.getBoundingClientRect().top -
              (positionY - elementToDrag.getBoundingClientRect().height / 2);

        setTextAreaHeight(partialHeight + textAreaToResize.getBoundingClientRect().height);
      }
    },
    [containerResizable, elementToDrag, textAreaToResize, isMouseDownOnElementDrag, maxTopPosition],
  );
  return {
    textAreaHeight,
    textAreaMaxHeight,
    isDragging,
    setIsMouseDownOnElementDrag,
    handleMouseMove,
  };
};
